/**
 * 角色权限分配抽屉
 * 使用 Ant Design Tree 组件展示权限树，支持勾选分配
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, Tree, Button, Space, Spin, Empty, Input } from 'antd';
import { message } from '@/utils/antdStatic';
import { SaveOutlined } from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';
import { getPermissionList } from '../../api/permission';
import { getRolePermissions, assignRolePermissions } from '../../api/role';
import type { Permission } from '../../api/types';

interface RolePermissionTreeProps {
  visible: boolean;
  roleId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

/** 将权限列表转换为 Tree 组件的 DataNode 结构 */
const convertToTreeData = (permissions: Permission[]): DataNode[] => {
  return permissions.map((perm) => {
    const node: DataNode = {
      key: perm.id,
      title: perm.title || perm.name,
      children: perm.children?.length ? convertToTreeData(perm.children) : undefined,
    };
    return node;
  });
};

/** 递归收集所有节点 key */
const collectAllKeys = (nodes: DataNode[]): string[] => {
  const keys: string[] = [];
  const traverse = (list: DataNode[]) => {
    for (const node of list) {
      keys.push(String(node.key));
      if (node.children && node.children.length > 0) {
        traverse(node.children);
      }
    }
  };
  traverse(nodes);
  return keys;
};

const RolePermissionTree: React.FC<RolePermissionTreeProps> = ({
  visible,
  roleId,
  onClose,
  onSuccess,
}) => {
  const [treeData, setTreeData] = useState<DataNode[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  const [halfCheckedKeys, setHalfCheckedKeys] = useState<string[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // 加载权限树
  const fetchPermissionTree = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPermissionList({ limit: 9999 });
      if (res?.data) {
        const data = res.data as any;
        const permissions: Permission[] = Array.isArray(data) ? data : data.data || data.list || [];
        const tree = convertToTreeData(permissions);
        setTreeData(tree);
        // 默认展开所有节点
        setExpandedKeys(collectAllKeys(tree));
      }
    } catch (error) {
      console.error('获取权限树失败', error);
      message.error('获取权限树失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 加载角色已有权限
  const fetchRolePermissions = useCallback(async () => {
    if (!roleId) return;

    try {
      const res = await getRolePermissions(roleId);
      if (res?.data) {
        const permIds = res.data.map((p) => String(p.id));
        // Tree 组件的 checkedKeys 只需传入叶子节点，父节点会自动计算
        // 为了兼容，直接设置所有已有权限 ID
        setCheckedKeys(permIds);
      }
    } catch (error) {
      console.error('获取角色权限失败', error);
    }
  }, [roleId]);

  useEffect(() => {
    if (visible && roleId) {
      fetchPermissionTree();
      fetchRolePermissions();
      setSearchValue('');
    }
  }, [visible, roleId, fetchPermissionTree, fetchRolePermissions]);

  // 选中变化
  const handleCheck = (
    checked: any,
    info: any,
  ) => {
    // checked 可能是 { checked: string[], halfChecked: string[] } 或 string[]
    if (Array.isArray(checked)) {
      setCheckedKeys(checked as string[]);
      setHalfCheckedKeys([]);
    } else {
      setCheckedKeys(checked.checked as string[]);
      setHalfCheckedKeys(checked.halfChecked as string[]);
    }
  };

  // 展开/收起
  const handleExpand = (keys: React.Key[]) => {
    setExpandedKeys(keys as string[]);
  };

  // 全选
  const handleCheckAll = () => {
    const allKeys = collectAllKeys(treeData);
    setCheckedKeys(allKeys);
  };

  // 全不选
  const handleUncheckAll = () => {
    setCheckedKeys([]);
    setHalfCheckedKeys([]);
  };

  // 展开全部
  const handleExpandAll = () => {
    setExpandedKeys(collectAllKeys(treeData));
  };

  // 收起全部
  const handleCollapseAll = () => {
    setExpandedKeys([]);
  };

  // 保存权限分配
  const handleSave = async () => {
    if (!roleId) return;

    setSaving(true);
    try {
      // 合并 checkedKeys 和 halfCheckedKeys 作为全部需要分配的权限
      const allPermissionIds = [...checkedKeys, ...halfCheckedKeys];
      await assignRolePermissions(roleId, allPermissionIds);
      message.success('权限分配成功');
      onSuccess();
    } catch (error) {
      console.error('权限分配失败', error);
      message.error('权限分配失败');
    } finally {
      setSaving(false);
    }
  };

  // 搜索过滤（高亮匹配）
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  // 递归标记搜索匹配的节点标题
  const highlightTreeData = (nodes: DataNode[], keyword: string): DataNode[] => {
    if (!keyword) return nodes;

    return nodes.map((node) => {
      const title = String(node.title || '');
      const index = title.toLowerCase().indexOf(keyword.toLowerCase());

      const highlightedTitle =
        index > -1 ? (
          <span>
            {title.substring(0, index)}
            <span style={{ color: '#f50', fontWeight: 'bold' }}>
              {title.substring(index, index + keyword.length)}
            </span>
            {title.substring(index + keyword.length)}
          </span>
        ) : (
          <span>{title}</span>
        );

      return {
        ...node,
        title: highlightedTitle,
        children: node.children ? highlightTreeData(node.children, keyword) : undefined,
      };
    });
  };

  const displayTreeData = searchValue ? highlightTreeData(treeData, searchValue) : treeData;

  return (
    <Drawer
      title="分配权限"
      placement="right"
      width={480}
      open={visible}
      onClose={onClose}
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
        >
          保存
        </Button>
      }
    >
      <Spin spinning={loading}>
        {/* 搜索框 */}
        <Input.Search
          placeholder="搜索权限名称"
          value={searchValue}
          onChange={handleSearch}
          style={{ marginBottom: 12 }}
          allowClear
        />

        {/* 操作按钮 */}
        <Space style={{ marginBottom: 12 }} wrap>
          <Button size="small" onClick={handleExpandAll}>
            展开全部
          </Button>
          <Button size="small" onClick={handleCollapseAll}>
            收起全部
          </Button>
          <Button size="small" onClick={handleCheckAll}>
            全选
          </Button>
          <Button size="small" onClick={handleUncheckAll}>
            取消全选
          </Button>
        </Space>

        {/* 权限树 */}
        {treeData.length > 0 ? (
          <Tree
            checkable
            checkStrictly={false}
            checkedKeys={checkedKeys}
            expandedKeys={expandedKeys}
            onCheck={handleCheck}
            onExpand={handleExpand}
            treeData={displayTreeData}
            height={500}
            style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}
          />
        ) : (
          !loading && <Empty description="暂无权限数据" />
        )}

        {/* 统计信息 */}
        <div style={{ marginTop: 16, color: '#999', fontSize: 12, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
          已选权限: {checkedKeys.length} 项
          {halfCheckedKeys.length > 0 && `（含 ${halfCheckedKeys.length} 个半选父节点）`}
        </div>
      </Spin>
    </Drawer>
  );
};

export default RolePermissionTree;
